import React, { useMemo, useRef } from 'react';
import * as d3 from 'd3';

type Point = { x: number; y: number };

export const FFR_SHARED_MARGIN = {
  top: 24,
  right: 42,
  bottom: 30,
  left: 34,
};

interface FFRChartProps {
  width: number;
  height: number;
  data: Point[];
  currentIndex?: number;
  title?: string;
  margin?: { top: number; right: number; bottom: number; left: number };
  uiScale?: number;
  onTransientIndexStart?: (idx: number) => void;
  onTransientIndexChange?: (idx: number) => void;
  onTransientIndexEnd?: () => void;
}

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const FFRChart: React.FC<FFRChartProps> = ({
  width,
  height,
  data,
  currentIndex = -1,
  title = 'FFR',
  margin = FFR_SHARED_MARGIN,
  uiScale = 1,
  onTransientIndexStart,
  onTransientIndexChange,
  onTransientIndexEnd,
}) => {
  const draggingRef = useRef(false);
  const chartScale = clamp(uiScale, 1, 1.8);
  const axisFontSize = Math.round(10 * chartScale);
  const valueFontSize = Math.round(11 * chartScale);
  const titleFontSize = Math.round(12 * chartScale);
  const lineStrokeWidth = 1.5 * chartScale;
  const labelY = Math.round(14 * chartScale);

  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const xDomain = useMemo<[number, number]>(() => {
    if (!data.length) return [0, 100];
    const e = d3.extent(data, (d) => d.x) as [number, number];
    if (!Number.isFinite(e[0]) || !Number.isFinite(e[1]) || e[0] === e[1]) {
      return [0, 100];
    }
    return e;
  }, [data]);

  const xScale = useMemo(
    () => d3.scaleLinear().domain(xDomain).range([0, innerWidth]),
    [xDomain, innerWidth],
  );

  const yScale = useMemo(
    () => d3.scaleLinear().domain([0.5, 1.0]).range([innerHeight, 0]),
    [innerHeight],
  );

  const linePath = useMemo(() => {
    if (!data.length) return '';
    const line = d3
      .line<Point>()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y));
    return line(data) ?? '';
  }, [data, xScale, yScale]);

  const selectedPoint = useMemo(() => {
    if (!data.length || currentIndex < 0 || currentIndex >= data.length) {
      return null;
    }
    return data[currentIndex];
  }, [data, currentIndex]);

  const markerX = useMemo(() => {
    if (!selectedPoint) return null;
    return xScale(selectedPoint.x);
  }, [selectedPoint, xScale]);

  const getNearestIndex = (clientX: number, svgRect: DOMRect) => {
    if (!data.length || innerWidth <= 0) return -1;

    const localX = clamp(clientX - svgRect.left - margin.left, 0, innerWidth);

    let nearestIndex = 0;
    let minDist = Infinity;

    for (let i = 0; i < data.length; i++) {
      const px = xScale(data[i].x);
      const dist = Math.abs(px - localX);
      if (dist < minDist) {
        minDist = dist;
        nearestIndex = i;
      }
    }

    return nearestIndex;
  };

  const emitIndex = (
    e: React.PointerEvent<SVGRectElement>,
    cb?: (idx: number) => void,
  ) => {
    const svgRect = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
    if (!svgRect) return;

    const idx = getNearestIndex(e.clientX, svgRect);
    if (idx >= 0) cb?.(idx);
  };

  const valueLabel = selectedPoint ? selectedPoint.y.toFixed(2) : '';

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{
        backgroundColor: '#0D0D0D',
        userSelect: 'none',
        touchAction: 'none',
        display: 'block',
      }}
    >
      <g transform={`translate(${margin.left},${margin.top})`}>
        <g transform={`translate(0,${innerHeight})`}>
          {xScale.ticks(Math.max(2, Math.floor(width / 120))).map((t, i) => (
            <text
              key={`x-${i}`}
              x={xScale(t)}
              y={Math.round(18 * chartScale)}
              fill="#9ca3af"
              textAnchor="middle"
              fontSize={axisFontSize}
            >
              {Math.round(t)}
            </text>
          ))}
        </g>

        {yScale.ticks(5).map((t, i) => (
          <g key={`y-${i}`}>
            <line
              x1={0}
              x2={innerWidth}
              y1={yScale(t)}
              y2={yScale(t)}
              stroke="#2a2f36"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            <text
              x={-6}
              y={yScale(t) + 3}
              fill="#9ca3af"
              textAnchor="end"
              fontSize={axisFontSize}
            >
              {t.toFixed(1)}
            </text>
          </g>
        ))}

        {linePath && (
          <path d={linePath} fill="none" stroke="#ffffff" strokeWidth={lineStrokeWidth} />
        )}

        {markerX !== null && selectedPoint && (
          <>
            <line
              x1={markerX}
              x2={markerX}
              y1={0}
              y2={innerHeight}
              stroke="#33FF33"
              strokeWidth={lineStrokeWidth}
            />
            <text
              x={markerX + Math.round(6 * chartScale)}
              y={labelY}
              textAnchor="start"
              fill="#ffffff"
              fontSize={valueFontSize}
              fontWeight={700}
            >
              {valueLabel}
            </text>
          </>
        )}

        <rect
          x={0}
          y={0}
          width={innerWidth}
          height={innerHeight}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
          onPointerDown={(e) => {
            draggingRef.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
            emitIndex(e, onTransientIndexStart);
          }}
          onPointerMove={(e) => {
            if (!draggingRef.current) return;
            emitIndex(e, onTransientIndexChange);
          }}
          onPointerUp={(e) => {
            draggingRef.current = false;
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
              e.currentTarget.releasePointerCapture(e.pointerId);
            }
            onTransientIndexEnd?.();
          }}
          onPointerCancel={(e) => {
            draggingRef.current = false;
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
              e.currentTarget.releasePointerCapture(e.pointerId);
            }
            onTransientIndexEnd?.();
          }}
        />
      </g>

      <text
        x={width / 2}
        y={Math.round(14 * chartScale)}
        textAnchor="middle"
        fill="#ffffff"
        fontSize={titleFontSize}
        fontWeight={700}
      >
        {title}
      </text>
    </svg>
  );
};

export default FFRChart;
